'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { lineToPath } from '../lib/tapestryText';
import TapestryReveal from './TapestryReveal';
import { useTapestrySkin } from '../lib/tapestrySkin';
import { useTapestryStagger } from '../lib/tapestryStagger';
import { useSeenReveal } from '../lib/seenReveal';

// How long the chalk reveal plays (matches keyframe durations in globals.css).
// We keep the CSS class applied for a slightly longer window so the
// animation-fill-mode "both" final state stays in place at handoff.
const REVEAL_DURATION_MS = 1250;

/**
 * Renders a single symbol from the composition — fetches its SVG via the
 * injected image client and mounts it, forcing the art white and hiding the
 * rule/textpath guide geometry.
 *
 * Inner-symbol text fills (removed — TT-969)
 * -----------------------------------------
 * This component used to overlay a single library word (lemma) onto each
 * usable `textpath*` guide inside the symbol SVG, sourced from the symbol's
 * own master-library keywords (name + tags + emotions). That was removed as
 * largely redundant — most symbols don't need a bare label word — pending
 * higher-quality short phrases from the backend that are better fill
 * candidates. The guide geometry is still hidden below, so reintroducing the
 * fill is a matter of restoring the overlay (see
 * docs/tdocs/TT-969-*.md for the full reintroduction guide). Note the SVG
 * `font-size` gotcha the old overlay worked around: inner text was rendered
 * as absolutely-positioned HTML spans (definitively CSS px) rather than SVG
 * `textPath`, whose font-size is ambiguous across browsers inside a
 * viewBox-scaled SVG.
 */

export default function TapestrySymbol({ placement, canvasHeight, imageClient }) {
  const hostRef = useRef(null);
  const [svgInfo, setSvgInfo] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [revealReady, setRevealReady] = useState(false);
  const stagger = useTapestryStagger();
  const seen = useSeenReveal();
  const skin = useTapestrySkin();
  // Whether THIS reveal chalked in (vs. shown straight because already seen).
  // Set by the reveal effect below; read at render time to pick the fade-in
  // style for seen (non-chalked) art.
  const chalkedRef = useRef(true);

  // Fetch the SVG for this symbol_id (preferring the curated slot's image
  // style, e.g. "minimal", when one is specified).
  const styleKey = placement.styles ? placement.styles.join(',') : '';
  useEffect(() => {
    let cancelled = false;
    imageClient.fetchSymbolImage(placement.symbolId, placement.styles).then((info) => {
      if (!cancelled) setSvgInfo(info);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement.symbolId, styleKey, imageClient]);

  // Kick off the chalk reveal once the symbol art is available — but
  // only after the stagger queue calls us back. Symbol-content rendering
  // is gated on `revealReady` so nothing is visible during the wait.
  // Cleanup releases the slot in both phases: pre-fire it removes our
  // queued item; post-fire it advances the queue immediately so a
  // mid-draw teardown doesn't stall the next element.
  const reservationRef = useRef(null);
  useLayoutEffect(() => {
    if (!svgInfo) return;
    // TT-961: a symbol that has already been introduced doesn't replay the
    // chalk reveal — no matter where it goes or if its curated image style
    // swaps. Show it straight (mount content, no queue, no chalk). New symbols
    // (or the same symbol at its own intro frame after a scrub-back) chalk in.
    const play = seen ? seen.reveal(placement.symbolId) : true;
    chalkedRef.current = play;
    if (!play) {
      setRevealReady(true);
      setRevealing(false);
      return;
    }
    let endT = null;
    const reservation = stagger?.schedule(REVEAL_DURATION_MS, () => {
      setRevealReady(true);
      setRevealing(true);
      endT = setTimeout(() => setRevealing(false), REVEAL_DURATION_MS);
    });
    reservationRef.current = reservation ?? null;
    return () => {
      if (endT) clearTimeout(endT);
      reservation?.cancel();
      reservationRef.current = null;
    };
  }, [svgInfo, stagger, seen, placement.symbolId]);

  // If a queued symbol gets removed before its turn, release the slot
  // immediately so the queue doesn't waste time on an invisible draw.
  useEffect(() => {
    if (placement.exiting && reservationRef.current) {
      reservationRef.current.cancel();
      reservationRef.current = null;
    }
  }, [placement.exiting]);

  // Mount the symbol SVG once when its source string changes. This does not
  // re-run on content changes — only when a different symbol arrives (which
  // effectively never happens because the parent keys each TapestrySymbol
  // instance by symbol_id). Gated on `revealReady` so the SVG is injected the
  // moment the symbol's queue turn arrives (its host div doesn't exist before
  // then).
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgInfo || !revealReady) return;
    const symbolContainer = host.querySelector('[data-symbol-art]');
    if (!symbolContainer) return;

    symbolContainer.innerHTML = svgInfo.svg;
    const svg = symbolContainer.querySelector('svg');
    if (!svg) return;

    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Strip any <style> blocks — they ship with `.st0 { stroke: #000 }`
    // rules that would override our white coloring and leak into the
    // document stylesheet.
    svg.querySelectorAll('style').forEach((s) => s.remove());

    // Force all symbol art to the skin's ink via inline styles (beats any classes)
    svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse').forEach((el) => {
      const curStroke = el.getAttribute('stroke');
      const curFill = el.getAttribute('fill');
      if (curStroke !== 'none') el.style.setProperty('stroke', skin.ink, 'important');
      if (curFill && curFill !== 'none') el.style.setProperty('fill', skin.ink, 'important');
    });

    // Hide rulepaths entirely
    svg.querySelectorAll('path, line').forEach((el) => {
      if (el.id && /^rulepath/i.test(el.id)) {
        el.style.visibility = 'hidden';
      }
    });

    // Hide textpath guides (previously the anchors for inner-symbol text
    // fills, removed in TT-969). Normalize <line>-based guides to <path> so a
    // future fill can read them uniformly without a mount-time conversion.
    svg.querySelectorAll('path, line, polyline').forEach((el) => {
      if (!el.id || !/^textpath/i.test(el.id)) return;
      if (el.nodeName === 'line') el = lineToPath(el);
      el.style.visibility = 'hidden';
    });
  }, [svgInfo, revealReady, skin.ink]);

  const px = placement.x * canvasHeight;
  const py = placement.y * canvasHeight;
  const size = placement.size * canvasHeight;

  return (
    <div
      ref={hostRef}
      className={`tapestry-symbol${revealing ? ' tapestry-revealing' : ''}`}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${size}px`,
        height: `${size}px`,
        // translate() in two parts so the -50% (half own size) is applied
        // after the pixel move. Animating transform is cheap and CSS
        // transitions redirect automatically when scrubbing.
        transform: `translate(${px}px, ${py}px) translate(-50%, -50%)`,
        opacity: placement.exiting ? 0 : 1,
        pointerEvents: 'none',
      }}
    >
      {/* Content wrapper — gets the fade-in animation during reveal.
          Mounted only when this symbol's stagger turn arrives, so nothing
          is visible while the symbol is queued. */}
      {revealReady && (
        <div
          // Seen symbols (shown without the chalk reveal) fade in rather than
          // popping when they remount somewhere new (TT-961). New symbols use
          // the chalk reveal's own content fade instead.
          className={`symbol-content${chalkedRef.current === false ? ' symbol-content-appear' : ''}`}
          style={{ position: 'absolute', inset: 0 }}
        >
          {/* Symbol art — the SVG gets injected into this div by the effect */}
          <div data-symbol-art="true" style={{ position: 'absolute', inset: 0 }} />
        </div>
      )}

      {/* Chalk + pen reveal overlay — only rendered during the first-mount
          animation window. Mount/unmount coincides with the CSS animation
          lifecycle so scrubbing cancels cleanly via React cleanup. */}
      {revealing && skin.chalk && <TapestryReveal seed={placement.symbolId} />}
    </div>
  );
}
